import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Calendar, ArrowLeft, Sparkles, HelpCircle } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const BlogPostPage = () => {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPost();
  }, [id]);

  const fetchPost = async () => {
    try {
      const response = await axios.get(`${API}/api/blog/${id}`);
      setPost(response.data);
    } catch (error) {
      console.error("Error fetching post:", error);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryTag = (category) => {
    switch (category) {
      case "fun_fact_friday":
        return (
          <span className="tag-friday inline-flex items-center gap-1 text-lg">
            <Sparkles className="w-4 h-4" />
            Fun Fact Friday
          </span>
        );
      case "trivia_tuesday":
        return (
          <span className="tag-tuesday inline-flex items-center gap-1 text-lg">
            <HelpCircle className="w-4 h-4" />
            Trivia Tuesday
          </span>
        );
      default:
        return (
          <span className="tag-general text-lg">
            General
          </span>
        );
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex justify-center items-center">
        <div className="w-16 h-16 border-4 border-[#E63946] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex justify-center items-center px-4">
        <div className="tactile-card p-12 text-center max-w-md">
          <h2 className="text-2xl font-bold text-[#1D3557] mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
            Post not found
          </h2>
          <Link to="/blog" className="btn-primary inline-flex items-center gap-2">
            <ArrowLeft className="w-5 h-5" />
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] py-12 px-4 md:px-8">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Back Link */}
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-[#1D3557] font-bold mb-8 hover:text-[#E63946] transition-colors"
            data-testid="back-to-blog-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Blog
          </Link>

          {/* Post Card */}
          <article className="tactile-card overflow-hidden" data-testid="blog-post-detail">
            {post.featured_image && (
              <img
                src={post.featured_image}
                alt={post.title}
                className="w-full h-64 md:h-96 object-cover"
              />
            )}
            <div className="p-8 md:p-12">
              <div className="mb-4">
                {getCategoryTag(post.category)}
              </div>
              <h1 className="text-3xl sm:text-4xl font-black text-[#1D3557] mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                {post.title}
              </h1>
              <div className="flex items-center gap-4 text-[#1D3557]/70 font-medium mb-8">
                <span className="flex items-center gap-1">
                  <Calendar className="w-5 h-5" />
                  {formatDate(post.created_at)}
                </span>
              </div>
              <div className="prose prose-lg max-w-none">
                <div className="text-[#1D3557] font-medium leading-relaxed whitespace-pre-wrap">
                  {post.content}
                </div>
              </div>
              {post.tags && post.tags.length > 0 && (
                <div className="mt-8 pt-8 border-t-4 border-[#1D3557]/10">
                  <div className="flex flex-wrap gap-2">
                    {post.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="px-4 py-2 bg-[#2A9D8F]/20 text-[#1D3557] rounded-full font-bold text-sm border-2 border-[#1D3557]"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </article>
        </motion.div>
      </div>
    </div>
  );
};

export default BlogPostPage;
